import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Contentcopy } from '@dsny/dsny-component-library/icons'
import { useTranslation } from 'react-i18next'
import { formatSeconds } from 'utils/stringHelpers'
import {
  MetaWrapper,
  DetailsWrapper,
  LyricsWrapper,
  ContentDetails,
  GenreDetails,
  RowWrapper,
  Label,
  Formats,
  Details,
  Title,
  Lyrics,
  ReadSection,
  CopyableValue,
  CopyButton,
  CopiedTooltip
} from './MetaData.styles.js'
import useReleaseInformationResponsiveBreakpointsV2 from '../../hooks/useReleaseInformationResponsiveBreakpointsV2'

function CopyValueButton({ value, label }) {
  const { t } = useTranslation()
  const [isCopied, setIsCopied] = useState(false)
  const copiedTimeoutRef = useRef(null)

  const copyValue = async (event) => {
    event.stopPropagation()
    await navigator.clipboard?.writeText(value)
    setIsCopied(true)
    window.clearTimeout(copiedTimeoutRef.current)
    copiedTimeoutRef.current = window.setTimeout(() => {
      setIsCopied(false)
    }, 1500)
  }

  useEffect(
    () => () => {
      window.clearTimeout(copiedTimeoutRef.current)
    },
    []
  )

  return (
    <CopyButton
      aria-label={t('release-info.copy-value', { label })}
      type="button"
      onClick={copyValue}
    >
      <Contentcopy />
      {isCopied && <CopiedTooltip>{t('release-info.copied')}</CopiedTooltip>}
    </CopyButton>
  )
}

CopyValueButton.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired
}

export default function MetaData({ track }) {
  const { t } = useTranslation()
  const responsiveBreakpointsV2 = useReleaseInformationResponsiveBreakpointsV2()
  const {
    album,
    genres,
    labels,
    duration,
    language,
    composer,
    isrc,
    upcnumber,
    catalognumber,
    copyrightyear,
    canadian,
    writers,
    publishing,
    producer,
    lyrics
  } = track
  const shouldDisplayOverview =
    album || composer || labels || duration || language
  const shouldDisplayIdentification =
    isrc || upcnumber || catalognumber || copyrightyear > 0
  const shouldDisplayCredits = writers || publishing || producer
  const [isReadMore, setIsReadMore] = useState(true)
  const formatValue = genres?.join(' / ')
  const toggleReadMore = () => {
    setIsReadMore(!isReadMore)
  }

  return (
    <MetaWrapper $responsiveBreakpointsV2={responsiveBreakpointsV2}>
      {shouldDisplayOverview && (
        <>
          <Title>Overview</Title>
          <DetailsWrapper $responsiveBreakpointsV2={responsiveBreakpointsV2}>
            {album && (
              <Label>
                ALBUM
                <Details>{album}</Details>
              </Label>
            )}
            {genres?.length > 0 && (
              <Label>
                FORMATS
                <CopyableValue>
                  <Formats>
                    {genres?.map((genre, index) => (
                      <GenreDetails key={index}>
                        {genre} {index < genres?.length - 1 && ' / '}
                      </GenreDetails>
                    ))}
                  </Formats>
                  <CopyValueButton
                    label={t('release-info.formats')}
                    value={formatValue}
                  />
                </CopyableValue>
              </Label>
            )}
            {labels && (
              <Label>
                LABELS
                {labels?.map((label, index) => (
                  <Details key={index}>{label.name}</Details>
                ))}
              </Label>
            )}
            {duration && (
              <Label>
                LENGTH
                <Details>{formatSeconds(duration)}</Details>
              </Label>
            )}
            {language && (
              <Label>
                LANGUAGE
                <Details>{language}</Details>
              </Label>
            )}
          </DetailsWrapper>
        </>
      )}

      {shouldDisplayIdentification && (
        <>
          <RowWrapper />
          <Title>Identification</Title>
          <DetailsWrapper $responsiveBreakpointsV2={responsiveBreakpointsV2}>
            {isrc && (
              <Label>
                ISRC
                <CopyableValue>
                  <Details>{isrc}</Details>
                  <CopyValueButton label="ISRC" value={isrc} />
                </CopyableValue>
              </Label>
            )}
            {upcnumber && (
              <Label>
                UPC
                <CopyableValue>
                  <Details>{upcnumber}</Details>
                  <CopyValueButton label="UPC" value={upcnumber} />
                </CopyableValue>
              </Label>
            )}
            {catalognumber && (
              <Label>
                CATALOG NUMBER
                <Details>{catalognumber}</Details>
              </Label>
            )}
            {copyrightyear > 0 && (
              <Label>
                COPYRIGHT YEAR
                <Details>{copyrightyear}</Details>
              </Label>
            )}
          </DetailsWrapper>
        </>
      )}

      {shouldDisplayCredits && (
        <>
          <RowWrapper />
          <Title>Credits</Title>
          <DetailsWrapper $responsiveBreakpointsV2={responsiveBreakpointsV2}>
            {canadian && (
              <Label>
                CANADIAN CONTENT
                {Object.entries(canadian).map(([key, item], i) => (
                  <ContentDetails key={i}>
                    {key.charAt(0).toUpperCase() + key.slice(1)} - {item}
                  </ContentDetails>
                ))}
              </Label>
            )}
            {writers && (
              <Label>
                WRITERS
                <Details>{writers}</Details>
              </Label>
            )}
            {publishing && (
              <Label>
                PUBLISHING
                <Details>{publishing}</Details>
              </Label>
            )}
            {composer && (
              <Label>
                COMPOSERS
                <Details>{composer}</Details>
              </Label>
            )}
            {producer && (
              <Label>
                PRODUCERS
                <Details>{producer}</Details>
              </Label>
            )}
          </DetailsWrapper>
        </>
      )}

      {lyrics && (
        <>
          <RowWrapper />
          <Title>Lyrics</Title>
          <LyricsWrapper>
            <CopyableValue>
              <Lyrics>{isReadMore ? lyrics.slice(0, 100) : lyrics}</Lyrics>
              <CopyValueButton
                label={t('release-info.lyrics')}
                value={lyrics}
              />
            </CopyableValue>
            <ReadSection onClick={toggleReadMore}>
              {isReadMore
                ? t('release-info.read-more')
                : t('release-info.read-less')}{' '}
            </ReadSection>
          </LyricsWrapper>
        </>
      )}
    </MetaWrapper>
  )
}

MetaData.propTypes = {
  track: PropTypes.shape({
    album: PropTypes.string,
    genres: PropTypes.arrayOf(PropTypes.string),
    labels: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string
      })
    ),
    duration: PropTypes.number,
    language: PropTypes.string,
    composer: PropTypes.string,
    isrc: PropTypes.string,
    upcnumber: PropTypes.string,
    catalognumber: PropTypes.string,
    copyrightyear: PropTypes.number,
    canadian: PropTypes.shape({}),
    writers: PropTypes.string,
    publishing: PropTypes.string,
    producer: PropTypes.string,
    lyrics: PropTypes.string
  }).isRequired
}
